import axios from 'axios';
import Swal from 'sweetalert2'
import {useUserStore} from "@/stores/user";
import {getActivePinia} from "pinia";
/**
 * Create a new Axios client instance
 * @see https://github.com/mzabriskie/axios#creating-an-instance
 */
const getClient = (baseUrl = null) => {
    const options = {
        baseURL: baseUrl,
        headers: {
            'Content-Type': 'application/json',
        }
    };
    // if (store.getters[getters.USER_IS_AUTHENTICATED]) {
    //     options.headers["Authorization"] = 'Bearer ' + store.getters[getters.USER_API_TOKEN]
    // }
    const client = axios.create(options);

    // Add a request interceptor
    const getUserStore = () => useUserStore(getActivePinia())

    client.interceptors.request.use(
          (requestConfig) => {
              const userStore = getUserStore()
              userStore.loadingCount++;
              return requestConfig
          },
          (requestError) => {
              const userStore = getUserStore()
              userStore.loadingCount--;
              return Promise.reject(requestError);
          },
    );

    client.interceptors.request.use(async (config) => {
        const userStore = getUserStore()
        if (!config._skipAuthRefresh && userStore.accessToken) {
            await userStore.ensureFreshSession('request')
        }

        if (userStore.accessToken) {
            config.headers = config.headers || {}
            config.headers.Authorization = `Bearer ${userStore.accessToken}`
        }

        return config
    });

    // Add a response interceptor
    client.interceptors.response.use(
      (response) => {
          const userStore = getUserStore()
          userStore.loadingCount--;
          return response
      },
      async (error) => {
          const userStore = getUserStore()
          userStore.loadingCount--;

          const originalRequest = error.config || {}
          const status = error.response?.status
          const requestUrl = originalRequest.url || ''
          const skipUnauthorizedHandling = originalRequest._skipUnauthorizedHandling

          if (status === 401 && !skipUnauthorizedHandling && !originalRequest._retry && !userStore.isAuthExcludedRequest(requestUrl)) {
              originalRequest._retry = true
              try {
                  const refreshedToken = await userStore.refreshTokenFromUnauthorized()
                  if (!refreshedToken) {
                      await userStore.handleUnauthorized({reason: 'session_expired'})
                      return Promise.reject(error)
                  }
                  originalRequest.headers = originalRequest.headers || {}
                  originalRequest.headers.Authorization = `Bearer ${refreshedToken}`
                  return client(originalRequest)
              } catch (refreshError) {
                  return Promise.reject(refreshError)
              }
          }

          if (status === 401 && !skipUnauthorizedHandling) {
              await userStore.handleUnauthorized({reason: 'session_expired'})
              return Promise.reject(error)
          }

          if (status === 401) {
              // caller opted into handling 401 itself (login, refresh, logout, ...).
              return Promise.reject(error)
          }

          if (status === 422) {
              return Promise.reject(error);
          }

          console.error('api request failed', {
              url: requestUrl,
              status,
              message: error.message,
          })

          if (!originalRequest._skipErrorAlert) {
              Swal.fire(
                  'Fehler',
                  error.response?.data?.message || error.message,
                  'error'
              )
          }
          return Promise.reject(error);
        },
    );

    return client;
};

class ApiClient {
    constructor(baseUrl = null) {
        this.client = getClient(baseUrl);
    }

    get(url, conf = {}) {
        return this.client.get(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }

    delete(url, conf = {}) {
        return this.client.delete(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }

    head(url, conf = {}) {
        return this.client.head(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }

    options(url, conf = {}) {
        return this.client.options(url, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }

    post(url, data = {}, conf = {}) {
        return this.client.post(url, data, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }

    put(url, data = {}, conf = {}) {
        return this.client.put(url, data, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }

    patch(url, data = {}, conf = {}) {
        return this.client.patch(url, data, conf)
            .then(response => Promise.resolve(response))
            .catch(error => Promise.reject(error));
    }
}

export {ApiClient};
